import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import type { PortalActivity, ProposalDownload } from '../types';

function mapDownloadSource(by?: string): PortalActivity['source'] {
  if (by === 'stakeholder') return 'stakeholder';
  if (by === 'employee' || by === 'admin') return 'employee';
  return 'client';
}

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

/** Load proposal PDF downloads from `proposalDownloads` as synthetic portal activity rows. */
export async function fetchHistoricalDownloadActivities(
  dealIds: string[],
  portalId: string,
): Promise<PortalActivity[]> {
  const uniqueDealIds = Array.from(new Set(dealIds.filter(Boolean)));
  if (uniqueDealIds.length === 0) return [];

  const docGroups = await Promise.all(
    uniqueDealIds.map(async (dealId) => {
      const snap = await getDocs(
        query(collection(db, 'proposalDownloads'), where('dealId', '==', dealId)),
      );
      return snap.docs;
    }),
  );

  const activities: PortalActivity[] = [];
  for (const docs of docGroups) {
    for (const d of docs) {
      const data = d.data() as ProposalDownload & { archived?: boolean };
      if (data.archived) continue;
      if (!data.dealId) continue;

      activities.push({
        id: `download:${d.id}`,
        portalId,
        projectId: data.dealId,
        sessionId: `download-${d.id}`,
        type: 'action',
        action: data.isSigned ? 'downloaded_signed_proposal' : 'downloaded_proposal',
        metadata: {
          totalAmount: data.totalAmount,
          isSigned: !!data.isSigned,
          downloadId: d.id,
          historical: true,
        },
        userAgent: data.userAgent,
        source: mapDownloadSource(data.downloadedBy),
        timestamp: toDate(data.downloadedAt),
      });
    }
  }

  return activities;
}

/** Same download logged to portalActivity and proposalDownloads — keep one row. */
export function portalDownloadEventsMatch(a: PortalActivity, b: PortalActivity): boolean {
  if (a.projectId !== b.projectId) return false;
  if (a.action !== b.action) return false;
  const amountA = a.metadata?.totalAmount;
  const amountB = b.metadata?.totalAmount;
  if (typeof amountA === 'number' && typeof amountB === 'number' && amountA !== amountB) {
    return false;
  }
  return Math.abs(a.timestamp.getTime() - b.timestamp.getTime()) <= 120_000;
}

export function mergePortalActivityWithHistoricalDownloads(
  liveEvents: PortalActivity[],
  historicalEvents: PortalActivity[],
): PortalActivity[] {
  const linkedDownloadIds = new Set<string>();
  for (const ev of liveEvents) {
    const downloadId = ev.metadata?.downloadId;
    if (typeof downloadId === 'string') linkedDownloadIds.add(downloadId);
  }

  const liveDownloadEvents = liveEvents.filter(
    (ev) => ev.action === 'downloaded_proposal' || ev.action === 'downloaded_signed_proposal',
  );

  const historicalFiltered = historicalEvents.filter((hist) => {
    const downloadId = hist.metadata?.downloadId || hist.id?.replace(/^download:/, '');
    if (downloadId && linkedDownloadIds.has(downloadId)) return false;
    if (liveDownloadEvents.some((live) => portalDownloadEventsMatch(live, hist))) return false;
    return true;
  });

  const byId = new Map<string, PortalActivity>();
  for (const ev of liveEvents) {
    if (ev.id) byId.set(ev.id, ev);
  }
  for (const ev of historicalFiltered) {
    if (ev.id) byId.set(ev.id, ev);
  }

  return Array.from(byId.values()).sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );
}
